import { SaleStatus } from '__generated__/globalTypes';

import { getBiddingClosedAt } from './salesUtils';

// Shared status-pill color maps, matching the badge palette PoolManager already uses for pool
// status (green/yellow/gray). PENDING_APPROVAL gets its own blue, distinct from CREATING's
// yellow -- a seller's freshly built sale sits there until approveSale opens it.
export const SALE_STATUS_PILL: Record<string, string> = {
  OPEN: 'bg-green-400 text-green-900',
  CREATING: 'bg-yellow-300 text-yellow-900',
  PENDING_APPROVAL: 'bg-blue-300 text-blue-900',
  FAILED: 'bg-error-muted text-white',
};

// Enum values read as-is are fine for OPEN/CREATING/FAILED, but not for a multi-word status --
// this is the one place every sale-status pill's label goes through, so a new enum value only
// needs a readable label added here, not at each render site.
export const SALE_STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open',
  CREATING: 'Creating',
  PENDING_APPROVAL: 'Pending approval',
  FAILED: 'Failed',
};

// The api never moves a sale off OPEN when bidding ends, so once every round's deadline has
// passed the pill reads "Round closed" instead.
export const saleStatusBadge = (sale: {
  status: string;
  rounds: { deadline_at: string }[];
}): { label: string; pillClass: string } =>
  sale.status === SaleStatus.OPEN && getBiddingClosedAt(sale.rounds)
    ? { label: 'Round closed', pillClass: 'bg-slate-200 text-slate-600' }
    : {
        label: SALE_STATUS_LABEL[sale.status] ?? sale.status,
        pillClass: SALE_STATUS_PILL[sale.status] ?? 'bg-slate-700 text-white',
      };

export const BIDDER_STATUS_PILL: Record<string, string> = {
  PENDING: 'bg-yellow-300 text-yellow-900',
  APPROVED: 'bg-green-400 text-green-900',
  REJECTED: 'bg-gray-900 text-white',
  REVOKED: 'bg-gray-900 text-white',
};

export const BIDDER_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  REVOKED: 'Revoked',
};

// ui-kit's primary fill and secondary text are brand-400, under 3:1 against a white or gray-100
// light-theme surface. Only for enabled buttons: the disabled styles bring their own fill.
// The pink-700 border also overrides the primary's only focus cue (focus:border-brand-500), so
// keyboard focus colors the transparent 2px outline that ui-kit's outline-none leaves in place.
export const PRIMARY_BUTTON_LIGHT_THEME =
  'light:border-pink-700 light:bg-pink-700 light:text-white light:hover:border-pink-800 light:hover:bg-pink-800 light:focus-visible:outline-pink-700';
export const SECONDARY_BUTTON_LIGHT_THEME =
  'light:border-pink-700 light:text-pink-700 light:hover:border-pink-800 light:hover:text-pink-800';
